import React, { memo, useCallback, useMemo } from 'react';

import useStableCallback from '../../hooks/useStableCallback';
import { normalizeId } from '../../utils/ids';

/** A row's key: its item id (stable, so antd's rows keep theirs). */
export const itemRowKey = item => normalizeId(item?._id);

// One cell's content, drawn again only when its column's render, its row's
// item, its value, its position or the date and time formats change.
const CellContent = memo(({ render, value, item, index }) => render(value, item, index));

/**
 * The columns with every cell's content memoised (KPR-1557). antd's Table
 * re-runs EVERY cell's render whenever it renders (antd bumps a render count
 * the cells' memo depends on), so a checkbox toggle redrew every cell of
 * every row. Each render here hands that call a memoised child instead: a
 * cell redraws when its column was rebuilt (ItemsTable's column memo is the
 * list of what a cell reads), its item changed, or `formats` changed — the
 * one thing cells read that no column depends on (the date and time format
 * preferences, read when a date is formatted).
 */
export function memoiseCellRenders(columns, formats) {
	return (columns || []).map(column => (typeof column?.render === 'function' ? {
		...column,
		render: (value, item, index) => (
			<CellContent render={column.render} value={value} item={item} index={index} formats={formats} />
		),
	} : column));
}

/**
 * The items table's per-row props, each keeping its identity between renders
 * (KPR-1557, R3.1): rebuilt every render, they were new props for every row
 * on every checkbox toggle. Each is rebuilt only when what it reads changes:
 *
 * - `rowSelection` on the selected ids, who may be selected, and the two
 *   selection reports. antd still redraws the checkbox column when the ids
 *   change — that is the change.
 * - `rowClassName` on the selected ids and the open row.
 * - `onRow` never: it reads the selection and the two handlers through
 *   stable wrappers that call the latest of each.
 *
 * The selection column, fixed beside the frozen title column. A row the
 * caller cannot write, or one whose record has closed, is not selectable —
 * an all-or-nothing apply would be refused BECAUSE of it, so offering the
 * checkbox would only be a way to break the batch.
 *
 * `onChange` is the funnel, not `onSelect`: antd runs its own shift-click
 * range over the checkbox column and reports THAT through `onChange` alone
 * (a range fires `onSelectMultiple`, never `onSelect`), so a handler hung
 * only on `onSelect` makes shift silently do nothing. `onSelect` is still
 * read, for the one thing `onChange` cannot say — which row the pointer was
 * on, i.e. the next range anchor.
 */
export default function useItemsTableRowProps({
	selectedIds,
	selectedItemId,
	selectionDisabledFor,
	onSelectionChange,
	onSelectionAnchor,
	onToggleSelect,
	onSelectItem,
}) {
	const rowSelection = useMemo(() => (selectedIds ? {
		selectedRowKeys: Array.from(selectedIds),
		// The ids survive a re-sort, a re-filter and a page that has scrolled
		// out of the data source.
		preserveSelectedRowKeys: true,
		fixed: 'left',
		columnWidth: 44,
		getCheckboxProps: (item) => {
			const reason = typeof selectionDisabledFor === 'function' ? selectionDisabledFor(item) : null;
			return reason ? { disabled: true, title: reason } : {};
		},
		onChange: keys => onSelectionChange?.(keys.map(key => `${key}`)),
		onSelect: item => onSelectionAnchor?.(normalizeId(item?._id)),
	} : undefined), [selectedIds, selectionDisabledFor, onSelectionChange, onSelectionAnchor]);

	const rowClassName = useCallback((item) => {
		const id = normalizeId(item?._id);
		return [
			id === selectedItemId ? 'items-table__row--selected' : '',
			selectedIds?.has(id) ? 'items-table__row--checked' : '',
		].filter(Boolean).join(' ');
	}, [selectedIds, selectedItemId]);

	// Shift-click a ROW extends the selection rather than opening it: a range
	// is the gesture, and opening the detail in the middle of picking one is
	// never what was meant.
	const rowClick = useStableCallback((item, event) => {
		if (selectedIds && event?.shiftKey) {
			event.preventDefault();
			const id = normalizeId(item?._id);
			const reason = typeof selectionDisabledFor === 'function' ? selectionDisabledFor(item) : null;
			if (!reason) onToggleSelect?.(id, { selected: true, shiftKey: true });
			return;
		}
		onSelectItem?.(item);
	});
	const onRow = useCallback(item => ({ onClick: event => rowClick(item, event) }), [rowClick]);

	return { rowSelection, rowClassName, onRow };
}
